import { FleetVehicle } from "services/api/fleet/vehicles/types";

/*---------------------------------------------------------------
   ENUMS
-----------------------------------------------------------------*/
export enum NotificationEntityType {
  FireExtinguisher = 1,
  VehicleRegistration = 2,
  VehicleInsurance = 3,
  Offer = 4,
  Contract = 5,
  Invoice = 6,
  Custom = 99,
}

export enum NotificationChannel {
  None = 0,
  Email = 1,
  SMS = 2,
  Push = 4,
}

export enum NotificationScope {
  Group = 0,
  Individual = 1,
}

/*---------------------------------------------------------------
   NOTIFICATION DEFINITION
-----------------------------------------------------------------*/
export interface NotificationDefinition {
  id: string;
  title: string;
  messageTemplate: string;
  entityType: NotificationEntityType;
  entityTypeName: string;
  scope: NotificationScope;
  channels: number;
  channelNames: string[];
  roles: NotificationDefinitionRole[];
  users: NotificationDefinitionUser[];
  excludedEntityIds: string[];
  includedEntityIds: string[];
  rule: NotificationRule | null;
  isCustom: boolean;
  customScheduledAt: string | null;
  notifyIfDateMissing: boolean;
  isActive: boolean;
  created: string;
  createdByUserId: string | null;
}

export interface NotificationDefinitionRole {
  roleId: string;
  isExcluded: boolean;
}

export interface NotificationDefinitionUser {
  userId: string;
  isExcluded: boolean;
}

export interface NotificationRule {
  daysBeforeExpiry: number;
  isRepeating: boolean;
  repeatIntervalDays: number | null;
  repeatCount: number | null;
}

/*---------------------------------------------------------------
   ROW (SEARCH TABLE)
-----------------------------------------------------------------*/
export interface NotificationDefinitionRow {
  id: string;
  title: string;
  messageTemplate: string;
  entityType: number;
  entityTypeName: string;
  scopeName: string;
  channelNames: string[];
  daysBeforeExpiry: number | null;
  isCustom: boolean;
  isRepeating: boolean;
  repeatIntervalDays: number | null;
  repeatCount: number | null;
  notifyIfDateMissing: boolean;
  customScheduledAt: string | null;
  recipientCount: number;
  isActive: boolean;
  created: string;
  childRows?: { id: string; entityId: string; displayName: string; entityType: number; parentEntityId?: string }[];
}

/*---------------------------------------------------------------
   FORM VALUES (WIZARD)
-----------------------------------------------------------------*/
export interface NotificationDefinitionFormValues {
  // Step 1 — Basic
  title: string;
  messageTemplate: string;
  entityType: NotificationEntityType | null;
  scope: NotificationScope;
  channels: number;
  isCustom: boolean;
  customScheduledAt: Date | null;

  // Step 2 — Recipients
  includedRoleIds: string[];
  includedUserIds: string[];
  excludedUserIds: string[];
  excludedEntityIds: string[];
  includedEntityIds: string[];

  // Step 3 — Rules
  daysBeforeExpiry: number | null;
  isRepeating: boolean;
  repeatIntervalDays: number | null;
  repeatCount: number | null;
  notifyIfDateMissing: boolean;
}

/*---------------------------------------------------------------
   SEARCH FORM
-----------------------------------------------------------------*/
export interface NotificationSearchFormValues {
  entityType: string | null;
  isActive: string | null;
  scope: string | null;
  /** Jedan kanal iz flags enuma — hvata sve notifikacije koje ga sadrže. */
  channel: string | null;
  isCustom: string | null;
  /** Pretraga po naslovu notifikacije. */
  search: string | null;
}

/*---------------------------------------------------------------
   LOG
-----------------------------------------------------------------*/
export interface NotificationLogSearchFormValues {
  vehicleId: string | null;
  channel: string | null;
  isSuccess: string | null;
}

export interface NotificationLog {
  id: string;
  /** Vozilo na koje se zapis odnosi; prazno kad ga tip entiteta nema. */
  vehicle: FleetVehicle | null;
  entityId: string | null;
  entityTypeName: string;
  entityDisplayName: string | null;
  recipientUserId: string;
  channelName: string;
  sentTitle: string;
  sentMessage: string;
  sentTries: number;
  sentOnUtc: string | null;
  isSuccess: boolean;
  errorMessage: string | null;
  isRead: boolean;
  created: string;
  definitionTitle: string | null;
}

/*---------------------------------------------------------------
   INBOX
-----------------------------------------------------------------*/
export interface NotificationInboxItem {
  id: string;
  sentTitle: string;
  sentMessage: string;
  entityDisplayName: string | null;
  /** Podaci za otvaranje entiteta klikom na obavijest. */
  entityId: string | null;
  entityType: number | null;
  /** Nadređeni entitet (vozilo) kad `entityId` sam ne vodi do rute. */
  parentEntityId: string | null;
  isRead: boolean;
  readAt: string | null;
  created: string;
}

export interface NotificationInboxResponse {
  unreadCount: number;
  items: NotificationInboxItem[];
}

/*---------------------------------------------------------------
   CALENDAR
-----------------------------------------------------------------*/
export interface NotificationCalendarDay {
  date: string;
  notifications: NotificationCalendarItem[];
}

export interface NotificationCalendarItem {
  id: string;
  title: string;
  /** Naslov same definicije, bez naziva entiteta. */
  definitionTitle: string;
  entityType: string;
  channels: string[];
  entityId: string | null;
  entityDisplayName: string | null;
  /** Datum isteka na koji se obavijest odnosi; prazno kod prilagođenih obavijesti. */
  relevantDate: string | null;
  daysBeforeExpiry: number;
  sendCount: number;
  repeatIntervalDays: number | null;
  /** Vozilo na koje se obavijest odnosi; prazno kad ga tip entiteta nema. */
  vehicle: FleetVehicle | null;
}

/*---------------------------------------------------------------
   CALENDAR — VREMENSKA CRTA PO VOZILU
-----------------------------------------------------------------*/
export interface VehicleTimelineItem {
  definitionId: string;
  title: string;
  entityDisplayName: string;
  entityType: string;
  channels: string[];
  /** Dan prvog slanja (datum isteka umanjen za `daysBeforeExpiry`). */
  startDate: string;
  /** Dan zadnjeg ponavljanja. */
  endDate: string;
  sendCount: number;
}

export interface VehicleTimelineGroup {
  vehicle: FleetVehicle;
  items: VehicleTimelineItem[];
}

/*---------------------------------------------------------------
   ENTITY TYPES / ITEMS
-----------------------------------------------------------------*/
export interface EntityTypeOption {
  value: number;
  name: string;
}

export interface EntityItem {
  id: string;
  displayName: string;
  relevantDate: string | null;
  hasMissingDate: boolean;
}
